import React, { createContext, useContext } from "react";

import { THEME as C } from "./dashboardTheme";

/**
 * True when the panel is rendered inside a Settings section page
 * (views/Settings/SettingsSectionPage.js), which already draws the page band.
 * The panel then renders a compact heading instead of a second band, so the
 * same editor serves its old standalone route and the Settings menu unchanged.
 */
export const SettingsEmbedContext = createContext(false);

/**
 * The page shell every new settings screen should use.
 * ────────────────────────────────────────────────────────────────────────────
 * Reproduces the cart screen's layout — tinted page, navy gradient header band
 * with an icon tile and an optional action on the right, then white panels with
 * an uppercase strip. Extracted so the next settings screen is one import
 * rather than another 60 lines of inline style objects.
 *
 *   <SettingsPanel
 *     icon="fa-money-bill-wave"
 *     title="Cash on Delivery — Price Limit"
 *     subtitle="Applies to every vendor type"
 *     action={<button …>Reset</button>}
 *   >
 *     <SettingsPanel.Section title="Current limit" icon="fa-sliders-h">
 *       …
 *     </SettingsPanel.Section>
 *   </SettingsPanel>
 *
 * Icons are Font Awesome class suffixes because that is what the dashboard
 * already loads globally — no new icon dependency for a settings page.
 */
const SettingsPanel = ({
  icon = "fa-cog",
  title,
  subtitle,
  action = null,
  maxWidth = 760,
  children,
}) => {
  const embedded = useContext(SettingsEmbedContext);
  if (embedded) {
    return (
      <div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
            marginBottom: 16,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{title}</div>
            {subtitle ? (
              <div style={{ fontSize: 12, color: C.textLight, marginTop: 2 }}>{subtitle}</div>
            ) : null}
          </div>
          {action}
        </div>
        {children}
      </div>
    );
  }
  return (
  <div
    className="lk-settings-page"
    // The side gutter drops on a phone (--lk-panel-gutter), where the theme
    // already pads the page.
    style={{ background: C.bg, minHeight: "100vh", padding: "var(--lk-settings-page-padding, 28px) var(--lk-panel-gutter, 16px) 56px" }}
  >
    <div style={{ maxWidth, margin: "0 auto" }}>
      {/* ── Header band ── */}
      <div
        style={{
          background: `linear-gradient(135deg, ${C.header} 0%, ${C.headerTo} 100%)`,
          borderRadius: C.radius,
          padding: "var(--lk-settings-header-padding, 20px 28px)",
          marginBottom: 24,
          boxShadow: C.headerShadow,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0 }}>
          <div
            style={{
              width: 42,
              height: 42,
              borderRadius: C.radiusSm,
              flexShrink: 0,
              background: "rgba(44,123,229,0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <i className={`fas ${icon}`} style={{ color: "#7eb8f7", fontSize: 17 }} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: "#fff",
                letterSpacing: "-0.3px",
                marginBottom: 2,
              }}
            >
              {title}
            </div>
            {subtitle ? (
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>
                {subtitle}
              </div>
            ) : null}
          </div>
        </div>
        {action}
      </div>

      {children}
    </div>
  </div>
  );
};

/** A white panel with the uppercase strip across its top. */
const Section = ({ title, icon = null, children, style = {} }) => (
  <div
    style={{
      background: C.panel,
      borderRadius: C.radius,
      border: `1px solid ${C.border}`,
      boxShadow: C.shadow,
      overflow: "hidden",
      marginBottom: 20,
      ...style,
    }}
  >
    {title ? (
      <div
        style={{
          padding: "13px 20px",
          borderBottom: `1px solid ${C.border}`,
          background: C.stripBg,
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        {icon ? (
          <i className={`fas ${icon}`} style={{ color: C.blue, fontSize: 12 }} />
        ) : null}
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: C.textMid,
            textTransform: "uppercase",
            letterSpacing: "0.07em",
          }}
        >
          {title}
        </span>
      </div>
    ) : null}
    <div style={{ padding: "var(--lk-settings-body-padding, 18px 20px)", minWidth: 0 }}>{children}</div>
  </div>
);

/** A label/value row, matching the cart's Order Summary lines. */
const Row = ({ label, value, strong = false, muted = false }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "baseline",
      gap: 12,
      padding: "6px 0",
    }}
  >
    <span
      style={{
        fontSize: strong ? 14 : 12.5,
        fontWeight: strong ? 700 : 500,
        color: muted ? C.textLight : strong ? C.text : C.textMid,
      }}
    >
      {label}
    </span>
    <span
      style={{
        fontSize: strong ? 15 : 13,
        fontWeight: strong ? 700 : 600,
        color: muted ? C.textLight : C.text,
      }}
    >
      {value}
    </span>
  </div>
);

/** The cart's dashed separator. */
const Divider = () => (
  <div
    style={{
      borderTop: `1px dashed ${C.border}`,
      margin: "12px 0",
    }}
  />
);

/**
 * Primary action. Disabled state matches the cart's checkout button — flat
 * border-grey with muted text and no shadow, so it reads as unavailable rather
 * than merely faded.
 */
const Action = ({ disabled, children, tone = "blue", ...rest }) => {
  const tones = {
    blue: { from: C.blue, to: C.blueDeep, glow: "rgba(44,123,229,0.3)" },
    teal: { from: C.teal, to: "#008f7a", glow: "rgba(0,182,155,0.3)" },
  };
  const t = tones[tone] || tones.blue;
  return (
    <button
      type="button"
      disabled={disabled}
      style={{
        padding: "10px 22px",
        borderRadius: C.radiusSm,
        border: "none",
        cursor: disabled ? "not-allowed" : "pointer",
        background: disabled ? C.border : `linear-gradient(135deg, ${t.from} 0%, ${t.to} 100%)`,
        color: disabled ? C.textLight : "#fff",
        fontSize: 13.5,
        fontWeight: 700,
        boxShadow: disabled ? "none" : `0 3px 10px ${t.glow}`,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        transition: "opacity 0.15s",
      }}
      onMouseEnter={(e) => {
        if (!disabled) e.currentTarget.style.opacity = "0.88";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.opacity = "1";
      }}
      {...rest}
    >
      {children}
    </button>
  );
};

SettingsPanel.Section = Section;
SettingsPanel.Row = Row;
SettingsPanel.Divider = Divider;
SettingsPanel.Action = Action;

export { Section, Row, Divider, Action };
export default SettingsPanel;
